'use client';
import styles from './details.module.css';
import React, { useEffect, useRef, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faArrowUpRightFromSquare,
	faClock,
	faScaleBalanced,
	faSuitcaseMedical,
	faUsers,
} from '@awesome.me/kit-012d1b9af0/icons/classic/regular';
import MyMarkdown from '@/components/my-markdown/my-markdown';
import { TrialDetails } from '@scitrials-sst/core/Next/types';

export const Summary = ({
	summary,
}: {
	summary: TrialDetails['briefSummary'];
}) => {
	if (!summary) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='Summary' />
			<ExpandableCard>
				<MyMarkdown text={summary} />
			</ExpandableCard>
		</div>
	);
};

export const Detailed = ({
	detail,
}: {
	detail: TrialDetails['detailedDescription'];
}) => {
	if (!detail) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='Detailed Description' />
			<ExpandableCard>
				<MyMarkdown text={detail} />
			</ExpandableCard>
		</div>
	);
};

export const Eligibility = ({
	eligibility,
}: {
	eligibility: {
		gender: TrialDetails['sex'];
		minAge: TrialDetails['minimumAge'];
		maxAge: TrialDetails['maximumAge'];
		criteria: TrialDetails['eligibilityCriteria'];
		healthy: TrialDetails['healthyVolunteers'];
		studyPopulation: TrialDetails['studyPopulation'];
		samplingMethod: TrialDetails['samplingMethod'];
	};
}) => {
	return (
		<div className={styles.item}>
			<CardTitle title='Eligibility' />
			<ExpandableCard>
				<>
					<div className={styles['small']}>
						<p>Gender: {eligibility?.gender ?? 'unknown'}</p>
						<p>
							Minimum Age:{' '}
							{eligibility?.minAge ? `${eligibility?.minAge}` : 'N/A'}
						</p>
						<p>
							Maximum Age:{' '}
							{eligibility?.maxAge ? `${eligibility?.maxAge}` : 'N/A'}
						</p>
					</div>

					<MyMarkdown text={eligibility.criteria!} />

					<div className={styles['small']}>
						{eligibility?.studyPopulation && (
							<p>Study Pop: {eligibility?.studyPopulation}</p>
						)}
						{eligibility?.samplingMethod && (
							<p>
								Sampling Method:{' '}
								{eligibility?.samplingMethod.replaceAll('_', ' ')}
							</p>
						)}
						<p>Healthy Volunteers: {eligibility.healthy ? 'Yes' : 'No'}</p>
					</div>
				</>
			</ExpandableCard>
		</div>
	);
};

export const Enrollment = ({
	participant,
}: {
	participant: TrialDetails['enrollmentInfoCount'];
}) => {
	return (
		<div className={styles.item}>
			<CardTitle title='Enrollment' />
			<ExpandableCard>
				<p>
					{participant
						? `Anticipated enrollment: ${participant} Participant${
								participant > 1 ? 's' : ''
						  }`
						: 'No Participants'}
				</p>
			</ExpandableCard>
		</div>
	);
};

export const Interventions = ({
	armGroups,
	interventions,
}: {
	armGroups: NonNullable<TrialDetails['armsInterventionsModule']>['armGroups'];
	interventions: NonNullable<
		TrialDetails['armsInterventionsModule']
	>['interventions'];
}) => {
	if (!Array.isArray(armGroups) || armGroups.length === 0) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='Interventions' />
			<ExpandableCard>
				<table className={styles.table}>
					<thead className={styles.thead}>
						<tr className={styles.tr}>
							<th className={styles.th} style={{ width: '50%' }}>
								<p>Trial Groups</p>
								<FontAwesomeIcon icon={faUsers} size='lg' color='#fff' />
							</th>
							<th className={styles.th} style={{ width: '50%' }}>
								<p>Treatments</p>
								<FontAwesomeIcon
									icon={faSuitcaseMedical}
									size='lg'
									color='#fff'
								/>
							</th>
						</tr>
					</thead>
					<tbody className={styles.tbody}>
						{armGroups.map((armGroup, i) => {
							const relatedInterventions = interventions?.filter(
								(intervention) =>
									intervention?.armGroupLabels?.includes(armGroup?.label ?? '')
							);

							return (
								<tr key={i} className={styles.tr}>
									<td className={styles.td}>
										<p className={styles.type}>
											{armGroup?.type
												? `${armGroup?.type
														.replaceAll('_', ' ')
														.toLowerCase()}: `
												: ''}
											{armGroup?.label ? `${armGroup?.label}` : ''}
										</p>
										<p>{armGroup?.description}</p>
									</td>
									<td className={styles.td}>
										{relatedInterventions?.map((intervention, j) => (
											<div key={j}>
												<p className={styles.type}>
													{intervention?.type
														? `${intervention?.type
																.replaceAll('_', ' ')
																.toLowerCase()}`
														: ''}
													{intervention?.name ? `: ${intervention?.name}` : ''}
												</p>
												<p>{intervention?.description}</p>
											</div>
										))}
										{relatedInterventions?.length === 0 && (
											<p>No treatment info</p>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</ExpandableCard>
		</div>
	);
};

export const Outcomes = ({
	outcomes,
}: {
	outcomes: TrialDetails['outcomesModule'];
}) => {
	return (
		<>
			<PrimaryOutcomes data={outcomes?.primaryOutcomes} />
			<SecondaryOutcomes data={outcomes?.secondaryOutcomes} />
		</>
	);
};

export const PrimaryOutcomes = ({
	data,
}: {
	data: NonNullable<TrialDetails['outcomesModule']>['primaryOutcomes'];
}) => {
	if (!data || data?.length === 0) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='Primary Outcomes' />
			<ExpandableCard>
				<table className={styles.table}>
					<thead className={styles.thead}>
						<tr className={styles.tr}>
							<th
								className={styles.index}
								style={{ width: '4%', padding: '0' }}
							/>
							<th className={styles.th} style={{ width: '25%' }}>
								<p>Measurement</p>
								<FontAwesomeIcon
									icon={faScaleBalanced}
									size='lg'
									color='#fff'
								/>
							</th>
							<th className={styles.th} style={{ width: '46%' }} />
							<th className={styles.th} style={{ width: '25%' }}>
								<p>When</p>
								<FontAwesomeIcon icon={faClock} size='lg' color='#fff' />
							</th>
						</tr>
					</thead>
					<tbody className={styles.tbody}>
						{data?.map((col, i) => (
							<tr key={i} className={styles.tr}>
								<td className={styles.index}>{i + 1}</td>
								<td className={styles.td}>{col?.measure}</td>
								<td className={styles.td}>{col?.description}</td>
								<td className={styles.td}>{col?.timeFrame}</td>
							</tr>
						))}
					</tbody>
				</table>
			</ExpandableCard>
		</div>
	);
};

export const SecondaryOutcomes = ({
	data,
}: {
	data: NonNullable<TrialDetails['outcomesModule']>['primaryOutcomes'];
}) => {
	if (!data || !data.length) return null;
	return (
		<div className={styles.item}>
			<CardTitle title='Secondary Outcomes' />
			<ExpandableCard>
				<table className={styles.table}>
					<thead className={styles.thead}>
						<tr className={styles.tr}>
							<th
								className={styles.index}
								style={{ width: '4%', padding: '0' }}
							/>
							<th className={styles.th} style={{ width: '25%' }}>
								<p>Measurement</p>
								<FontAwesomeIcon
									icon={faScaleBalanced}
									size='lg'
									color='#fff'
								/>
							</th>
							<th className={styles.th} style={{ width: '46%' }} />
							<th className={styles.th} style={{ width: '25%' }}>
								<p>When</p>
								<FontAwesomeIcon icon={faClock} size='lg' color='#fff' />
							</th>
						</tr>
					</thead>
					<tbody className={styles.tbody}>
						{data?.map((col, i) => (
							<tr key={i} className={styles.tr}>
								<td className={styles.index}>{i + 1}</td>
								<td className={styles.td}>{col?.measure}</td>
								<td className={styles.td}>{col?.description}</td>
								<td className={styles.td}>{col?.timeFrame}</td>
							</tr>
						))}
					</tbody>
				</table>
			</ExpandableCard>
		</div>
	);
};

export const TrialControlGroup = ({
	controlGroup,
	controlGroupFullTreatment,
	controlGroupText,
}: {
	controlGroup: NonNullable<TrialDetails['curated']>['controlGroup'];
	controlGroupFullTreatment: NonNullable<
		TrialDetails['curated']
	>['controlGroupFullTreatment'];
	controlGroupText: NonNullable<TrialDetails['curated']>['controlGroupText'];
}) => {
	if (controlGroup || controlGroupFullTreatment)
		return (
			<div className={styles.item}>
				<CardTitle title='Trial Control Group' />
				<ExpandableCard>
					{!controlGroup || controlGroup === 'no' ? (
						<p>There is no control group</p>
					) : (
						<>
							<p>There is a control group</p>
							<div className={`${styles.small} ${styles.li}`}>
								{controlGroupFullTreatment === 'yes' ? (
									<li>
										If you enter the control group you will be offered the full
										treatment at the end of the trial.
									</li>
								) : controlGroupFullTreatment === 'no' ? (
									<li>
										If you enter the control group you will be not be
										automatically offered the full treatment at the end of the
										trial.
									</li>
								) : controlGroupFullTreatment === 'maybe' ? (
									<li>
										It is currently undecided if you will be offered the full
										treatment at the end of the trial.
									</li>
								) : null}
								{controlGroupText && <li>{controlGroupText}</li>}
							</div>
						</>
					)}
				</ExpandableCard>
			</div>
		);
};

export const Conditions = ({
	conditions,
}: {
	conditions: TrialDetails['conditions'];
}) => {
	if (!conditions || conditions.length === 0) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='Conditions' />
			<ExpandableCard>
				<ul className={`${styles.ul} ${styles.small}`}>
					{conditions?.map((condition) => (
						<li key={condition}>{condition}</li>
					))}
				</ul>
			</ExpandableCard>
		</div>
	);
};

export const Sponsors = ({
	sponsors,
}: {
	sponsors: TrialDetails['leadSponsorName'];
}) => {
	if (!sponsors) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='Sponsors' />
			<ExpandableCard>
				<ul className={`${styles.ul} ${styles.small}`}>
					<li>{sponsors}</li>
				</ul>
			</ExpandableCard>
		</div>
	);
};

export function References({
	references,
}: {
	references: TrialDetails['references'];
}) {
	if (!references || references?.length === 0) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='References' />
			<ExpandableCard>
				<ul className={`${styles.ul} ${styles.small}`}>
					{references?.map((reference, i) => (
						<li key={i}>
							PMID: {reference?.pmid}: {reference?.citation}
						</li>
					))}
				</ul>
			</ExpandableCard>
		</div>
	);
}

export const ExtraLink = ({
	links,
}: {
	links: TrialDetails['seeAlsoLinks'];
}) => {
	if (!links || links.length === 0) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='Extra Link' />
			<ExpandableCard>
				<ul className={`${styles.ul} ${styles.small}`}>
					{links.map((reference) => (
						<li key={reference?.url}>
							<a
								href={reference?.url}
								target='_blank'
								rel='noopener noreferrer'
							>
								{reference?.label}{' '}
								<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
							</a>
						</li>
					))}
				</ul>
			</ExpandableCard>
		</div>
	);
};

export const StudyDocument = ({
	documents,
}: {
	documents: TrialDetails['documents'];
}) => {
	if (!documents || documents.length === 0) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='Study Documents' />
			<ExpandableCard>
				<ul className={`${styles.ul} ${styles.small}`}>
					{documents?.map((study) => (
						<li key={study?.url}>
							<a href={study?.url} target='_blank' rel='noopener noreferrer'>
								{study?.label}{' '}
								<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
							</a>
						</li>
					))}
				</ul>
			</ExpandableCard>
		</div>
	);
};

export const Collaborators = ({
	collabs,
}: {
	collabs: TrialDetails['collaborators'];
}) => {
	if (!collabs || collabs.length === 0) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='Collaborators' />
			<ExpandableCard>
				<ul className={`${styles.ul} ${styles.small}`}>
					{collabs?.map((collab, i) => (
						<li key={`${i}-${collab}`}>{collab?.name}</li>
					))}
				</ul>
			</ExpandableCard>
		</div>
	);
};

export const Keywords = ({ keys }: { keys: TrialDetails['keywords'] }) => {
	if (!keys || keys.length === 0) return null;

	return (
		<div className={styles.item}>
			<CardTitle title='Keywords' />
			<ExpandableCard>
				<ul className={`${styles.ul} ${styles.small}`}>
					{keys?.map((key) => (
						<li key={key}>{key}</li>
					))}
				</ul>
			</ExpandableCard>
		</div>
	);
};

export const CardTitle = ({ title }: { title: string }) => {
	return (
		<div className={styles.title}>
			<p>{title}</p>
		</div>
	);
};

export const ExpandableCard = ({ children }: { children: React.ReactNode }) => {
	const [expanded, setExpanded] = useState(false);
	const [contentHeight, setContentHeight] = useState<number | null>(null);
	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const el = contentRef.current;
		if (!el) return;

		const observer = new ResizeObserver(() => {
			setContentHeight(el.scrollHeight);
		});

		observer.observe(el);
		return () => observer.disconnect();
	}, [children]);

	return (
		<div className={styles.card}>
			<div
				ref={contentRef}
				style={{
					maxHeight: expanded ? contentHeight ?? 'none' : '6rem',
					overflow: 'hidden',
					transition: 'max-height 0.4s ease',
				}}
			>
				{children}
			</div>

			{(contentHeight ?? 0) > 112 && (
				<button
					onClick={() => setExpanded((prev) => !prev)}
					style={{
						color: '#446cb3',
						marginTop: '0.5rem',
						cursor: 'pointer',
					}}
				>
					{expanded ? 'SHOW LESS' : 'SHOW MORE...'}
				</button>
			)}
		</div>
	);
};
